if (!customElements.get('aha-member-registration')) {
  customElements.define(
    'aha-member-registration',
    class AhaMemberRegistration extends HTMLElement {
      constructor() {
        super();
        this.currentStep = 1;
        this.totalSteps = 7;
        this.lang = 'zh';
        this.otpToken = '';
        this.otpVerifiedPhone = '';
        this.otpSent = false;
        this.otpResendSeconds = 0;
        this.otpResendTimer = null;
      }

      disconnectedCallback() {
        this.clearOtpResendCooldown();
      }

      connectedCallback() {
        this.form = this.querySelector('form');
        this.successEl = this.querySelector('.aha-registration__success');
        this.formWrap = this.querySelector('.aha-registration__form');
        this.formError = this.querySelector('[data-form-error]');
        this.steps = this.querySelectorAll('[data-step]');
        this.progressFill = this.querySelector('[data-progress-fill]');
        this.progressLabel = this.querySelector('[data-progress-label]');
        this.backBtn = this.querySelector('[data-action="back"]');
        this.nextBtn = this.querySelector('[data-action="next"]');
        this.submitBtn = this.querySelector('[data-action="submit"]');
        this.deferCheckbox = this.querySelector('[data-defer]');
        this.contentCheckboxes = this.querySelectorAll('[data-content-pref]');
        this.lang = this.getUiLangFromLocale(this.dataset.shopLocale);
        this.apiUrl = this.dataset.apiUrl?.trim() || '';
        this.apiSecret = this.dataset.apiSecret?.trim() || '';
        this.otpIssueUrl = this.dataset.otpIssueUrl?.trim() || '';
        this.otpValidateUrl = this.dataset.otpValidateUrl?.trim() || '';
        this.countryCodesUrl = this.dataset.countryCodesUrl?.trim() || '';
        this.otpSendBtn = this.querySelector('[data-action="otp-send"]');
        this.otpValidateBtn = this.querySelector('[data-action="otp-validate"]');
        this.otpVerifyWrap = this.querySelector('[data-otp-verify]');
        this.otpStatusEl = this.querySelector('[data-otp-status]');
        this.phoneCountrySelect = this.querySelector('[name="aha_phone_country"]');
        this.phoneInput = this.querySelector('[name="aha_phone_local"]');
        this.otpInput = this.querySelector('[name="aha_otp_code"]');
        this.emailInput = this.querySelector('[name="aha_email"]');

        this.backBtn?.addEventListener('click', () => this.goBack());
        this.nextBtn?.addEventListener('click', () => this.goNext());
        this.form?.addEventListener('submit', (e) => this.onSubmit(e));
        this.otpSendBtn?.addEventListener('click', () => this.requestOtp());
        this.otpValidateBtn?.addEventListener('click', () => this.validateOtp());
        this.phoneInput?.addEventListener('input', () => this.onPhoneInputChange());
        this.phoneCountrySelect?.addEventListener('change', () => this.onPhoneInputChange());

        this.deferCheckbox?.addEventListener('change', () => this.onDeferChange());

        this.contentCheckboxes.forEach((cb) => {
          cb.addEventListener('change', () => {
            if (cb.checked && this.deferCheckbox) {
              this.deferCheckbox.checked = false;
            }
          });
        });

        this.initCountryCodes().finally(() => {
          this.updateStep();
          this.updateLanguage();
        });
      }

      async initCountryCodes() {
        if (!this.phoneCountrySelect) return;

        const fallback = [{ iso: 'hk', code: '852', name: 'Hong Kong (香港)' }];
        try {
          if (this.countryCodesUrl) {
            const response = await fetch(this.countryCodesUrl);
            if (!response.ok) throw new Error('Failed to load country codes');
            this.countryCodes = await response.json();
          } else {
            this.countryCodes = fallback;
          }
        } catch (_err) {
          this.countryCodes = fallback;
        }

        this.renderCountryCodeOptions();
      }

      isoToFlag(iso) {
        if (!iso || iso.length !== 2) return '';
        return iso
          .toUpperCase()
          .split('')
          .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
          .join('');
      }

      formatCountryOptionLabel({ iso, code }) {
        const flag = this.isoToFlag(iso);
        const sharedDialCode = this.countryCodes.filter((country) => country.code === code).length > 1;
        return sharedDialCode ? `${flag} +${code} ${iso.toUpperCase()}` : `${flag} +${code}`;
      }

      renderCountryCodeOptions() {
        if (!this.phoneCountrySelect || !this.countryCodes?.length) return;

        const preferredIsos = ['hk', 'cn', 'mo', 'tw', 'sg', 'us', 'gb', 'au'];
        const byIso = new Map(this.countryCodes.map((country) => [country.iso, country]));
        const preferred = preferredIsos.map((iso) => byIso.get(iso)).filter(Boolean);
        const preferredSet = new Set(preferredIsos);
        const rest = this.countryCodes.filter((country) => !preferredSet.has(country.iso));
        const ordered = [...preferred, ...rest];

        this.phoneCountrySelect.innerHTML = ordered
          .map((country) => {
            const { iso, code } = country;
            const selected = iso === 'hk' ? ' selected' : '';
            const label = this.formatCountryOptionLabel(country);
            return `<option value="${code}" data-iso="${iso}"${selected}>${label}</option>`;
          })
          .join('');
      }

      getSelectedCountryCode() {
        return String(this.phoneCountrySelect?.value || '852').replace(/\D/g, '');
      }

      getLocalPhoneDigits() {
        return String(this.phoneInput?.value || '').replace(/\D/g, '');
      }

      getCombinedPhone() {
        const countryCode = this.getSelectedCountryCode();
        const localDigits = this.getLocalPhoneDigits();
        if (!countryCode || !localDigits) return '';
        return `${countryCode}${localDigits}`;
      }

      getNormalizedPhone() {
        const combined = this.getCombinedPhone();
        if (!combined) return '';
        return this.normalizePhone(combined);
      }

      getUiLangFromLocale(isoCode) {
        const code = String(isoCode || document.documentElement.lang || 'zh-TW').toLowerCase();
        return code.startsWith('en') ? 'en' : 'zh';
      }

      getStrings() {
        const zh = {
          step: '步驟',
          of: '/',
          back: '上一步',
          next: '下一步',
          submit: '提交登記',
          errorRequired: '請填寫此欄',
          errorSelect: '請選擇一項',
          errorPhone: '請輸入有效的電話號碼',
          errorConsent: '請同意隱私政策及接收訊息',
          errorContent: '請選擇至少一項內容類型，或選擇「之後再設定」',
          errorApiMissing: '尚未設定註冊 API，請聯絡網站管理員。',
          errorSubmit: '提交失敗，請稍後再試。',
          errorDuplicate: '此電話號碼已登記，請使用其他號碼或登入現有帳戶。',
          errorEmail: '請輸入有效的電郵地址',
          errorDuplicateEmail: '此電郵地址已登記，請使用其他電郵或登入現有帳戶。',
          errorOtpIssueApiMissing: '尚未設定 OTP 發送 API，請聯絡網站管理員。',
          errorOtpValidateApiMissing: '尚未設定 OTP 驗證 API，請聯絡網站管理員。',
          errorOtpRequired: '請先完成電話 OTP 驗證',
          errorOtpInvalid: '驗證碼無效或已過期，請重新發送並再試',
          otpSent: '驗證碼已發送，請查收 WhatsApp 訊息',
          otpVerified: '電話已成功驗證',
          otpSend: '發送驗證碼',
          otpSending: '發送中...',
          otpResendWait: (seconds) => `${seconds} 秒後可重新發送`,
          otpVerify: '驗證電話',
          otpVerifying: '驗證中...',
        };
        const en = {
          step: 'Step',
          of: 'of',
          back: 'Back',
          next: 'Next',
          submit: 'Submit registration',
          errorRequired: 'This field is required',
          errorSelect: 'Please select an option',
          errorPhone: 'Please enter a valid phone number',
          errorConsent: 'Please agree to the privacy policy and messaging consent',
          errorContent: 'Please select at least one content type, or choose "Set later"',
          errorApiMissing: 'Registration API is not configured. Please contact the store administrator.',
          errorSubmit: 'Submission failed. Please try again later.',
          errorDuplicate: 'This phone number is already registered. Please use a different number or sign in.',
          errorEmail: 'Please enter a valid email address',
          errorDuplicateEmail: 'This email is already registered. Please use a different email or sign in.',
          errorOtpIssueApiMissing: 'OTP issue API is not configured. Please contact the store administrator.',
          errorOtpValidateApiMissing: 'OTP validate API is not configured. Please contact the store administrator.',
          errorOtpRequired: 'Please complete phone OTP verification first',
          errorOtpInvalid: 'Invalid or expired code. Please request a new code and try again.',
          otpSent: 'Verification code sent. Please check your WhatsApp message.',
          otpVerified: 'Phone verified successfully.',
          otpSend: 'Send verification code',
          otpSending: 'Sending...',
          otpResendWait: (seconds) => `Resend in ${seconds}s`,
          otpVerify: 'Verify phone',
          otpVerifying: 'Verifying...',
        };
        return this.lang === 'en' ? en : zh;
      }

      updateLanguage() {
        const strings = this.getStrings();
        this.querySelectorAll('[data-i18n]').forEach((el) => {
          const key = el.dataset.i18n;
          const zhText = el.dataset.i18nZh;
          const enText = el.dataset.i18nEn;
          const zhHtml = el.dataset.i18nHtmlZh;
          const enHtml = el.dataset.i18nHtmlEn;
          if (key === 'back') el.textContent = strings.back;
          else if (key === 'next') el.textContent = strings.next;
          else if (key === 'submit') el.textContent = strings.submit;
          else if (zhHtml && enHtml) {
            el.innerHTML = this.lang === 'en' ? enHtml : zhHtml;
          } else if (zhText && enText) {
            el.textContent = this.lang === 'en' ? enText : zhText;
          }
        });
        this.updateOtpSendButton();
        if (this.otpValidateBtn) this.otpValidateBtn.textContent = strings.otpVerify;
        this.updateProgressLabel();
      }

      clearOtpResendCooldown() {
        if (this.otpResendTimer) {
          clearInterval(this.otpResendTimer);
          this.otpResendTimer = null;
        }
        this.otpResendSeconds = 0;
        this.updateOtpSendButton();
      }

      startOtpResendCooldown(seconds = 60) {
        if (this.otpResendTimer) {
          clearInterval(this.otpResendTimer);
        }
        this.otpResendSeconds = seconds;
        this.updateOtpSendButton();
        this.otpResendTimer = setInterval(() => {
          this.otpResendSeconds -= 1;
          if (this.otpResendSeconds <= 0) {
            this.clearOtpResendCooldown();
          } else {
            this.updateOtpSendButton();
          }
        }, 1000);
      }

      updateOtpSendButton() {
        if (!this.otpSendBtn) return;
        const strings = this.getStrings();
        if (this.otpResendSeconds > 0) {
          this.otpSendBtn.disabled = true;
          this.otpSendBtn.textContent = strings.otpResendWait(this.otpResendSeconds);
          return;
        }
        this.otpSendBtn.disabled = false;
        this.otpSendBtn.textContent = strings.otpSend;
      }

      parseRegisterError(data, fallback) {
        if (data?.error) return data.error;
        const userMessages = (data?.userErrors || [])
          .map((entry) => entry?.message)
          .filter(Boolean);
        if (userMessages.length) return userMessages.join(' ');
        return fallback;
      }

      hasPhoneRegisterError(data) {
        const phoneFieldError = (data?.userErrors || []).some((entry) =>
          (entry?.field || []).some((field) => String(field).toLowerCase() === 'phone')
        );
        return phoneFieldError || /phone/i.test(data?.error || '');
      }

      hasEmailRegisterError(data) {
        const emailFieldError = (data?.userErrors || []).some((entry) =>
          (entry?.field || []).some((field) => String(field).toLowerCase() === 'email')
        );
        return emailFieldError || /email/i.test(data?.error || '');
      }

      getRequestHeaders() {
        const headers = { 'Content-Type': 'application/json' };
        if (this.apiSecret) {
          headers['X-Registration-Secret'] = this.apiSecret;
        }
        return headers;
      }

      setButtonLoading(button, loading, idleText, loadingText) {
        if (!button) return;
        button.disabled = loading;
        button.textContent = loading ? loadingText : idleText;
      }

      setOtpStatus(message, isError = false) {
        if (!this.otpStatusEl) return;
        this.otpStatusEl.textContent = message || '';
        this.otpStatusEl.classList.toggle('is-error', Boolean(isError && message));
      }

      setOtpVerifyVisible(visible) {
        if (!this.otpVerifyWrap) return;
        this.otpVerifyWrap.hidden = !visible;
        if (!visible && this.otpInput) {
          this.otpInput.value = '';
        }
      }

      isOtpVerifiedForCurrentPhone() {
        const phone = this.getNormalizedPhone();
        return Boolean(this.otpToken && this.otpVerifiedPhone === phone);
      }

      onPhoneInputChange() {
        this.otpToken = '';
        this.otpVerifiedPhone = '';
        this.otpSent = false;
        this.clearOtpResendCooldown();
        this.setOtpVerifyVisible(false);
        this.setOtpStatus('');
        this.updateStep();
      }

      async requestOtp() {
        if (this.otpResendSeconds > 0) return;

        this.clearFormError();
        const strings = this.getStrings();
        const stepEl = this.querySelector('[data-step="4"]');
        const phone = this.getNormalizedPhone();

        this.clearError(stepEl);
        this.setOtpStatus('');

        if (!phone || !this.isValidPhone(phone)) {
          this.showError(stepEl, strings.errorPhone);
          this.phoneInput?.focus();
          return;
        }
        if (!this.otpIssueUrl) {
          this.showError(stepEl, strings.errorOtpIssueApiMissing);
          return;
        }

        this.setButtonLoading(this.otpSendBtn, true, strings.otpSend, strings.otpSending);
        try {
          const response = await fetch(this.otpIssueUrl, {
            method: 'POST',
            headers: this.getRequestHeaders(),
            body: JSON.stringify({ phone, purpose: 'register' }),
          });
          const data = await response.json().catch(() => ({}));

          if (!response.ok || data.ok === false) {
            const message =
              response.status === 409 ? strings.errorDuplicate : data.error || strings.errorSubmit;
            this.showError(stepEl, message);
            return;
          }

          this.otpToken = '';
          this.otpVerifiedPhone = '';
          this.otpSent = true;
          this.setOtpVerifyVisible(true);
          this.setOtpStatus(strings.otpSent);
          this.startOtpResendCooldown(60);
          this.otpInput?.focus();
        } catch (_err) {
          this.showError(stepEl, strings.errorSubmit);
        } finally {
          if (this.otpResendSeconds <= 0) {
            this.setButtonLoading(this.otpSendBtn, false, strings.otpSend, strings.otpSending);
          } else {
            this.updateOtpSendButton();
          }
        }
      }

      async validateOtp() {
        this.clearFormError();
        const strings = this.getStrings();
        const stepEl = this.querySelector('[data-step="4"]');
        const phone = this.getNormalizedPhone();
        const otp = this.otpInput?.value.trim() || '';

        this.clearError(stepEl);

        if (!phone || !this.isValidPhone(phone)) {
          this.showError(stepEl, strings.errorPhone);
          this.phoneInput?.focus();
          return;
        }
        if (!/^\d{6}$/.test(otp)) {
          this.showError(stepEl, strings.errorOtpInvalid);
          this.otpInput?.focus();
          return;
        }
        if (!this.otpValidateUrl) {
          this.showError(stepEl, strings.errorOtpValidateApiMissing);
          return;
        }

        this.setButtonLoading(this.otpValidateBtn, true, strings.otpVerify, strings.otpVerifying);
        try {
          const response = await fetch(this.otpValidateUrl, {
            method: 'POST',
            headers: this.getRequestHeaders(),
            body: JSON.stringify({ phone, otp, purpose: 'register' }),
          });
          const data = await response.json().catch(() => ({}));

          if (!response.ok || !data?.token) {
            this.otpToken = '';
            this.otpVerifiedPhone = '';
            this.setOtpStatus(strings.errorOtpInvalid, true);
            this.showError(stepEl, data.error || strings.errorOtpInvalid);
            return;
          }

          this.otpToken = data.token;
          this.otpVerifiedPhone = phone;
          this.clearError(stepEl);
          this.setOtpStatus(strings.otpVerified);
          this.goNext();
        } catch (_err) {
          this.showError(stepEl, strings.errorSubmit);
        } finally {
          this.setButtonLoading(this.otpValidateBtn, false, strings.otpVerify, strings.otpVerifying);
        }
      }

      updateProgressLabel() {
        const strings = this.getStrings();
        if (this.progressLabel) {
          this.progressLabel.textContent =
            this.lang === 'en'
              ? `${strings.step} ${this.currentStep} ${strings.of} ${this.totalSteps}`
              : `${strings.step} ${this.currentStep} ${strings.of} ${this.totalSteps}`;
        }
      }

      normalizePhone(raw) {
        const digits = String(raw).replace(/\D/g, '');
        if (!digits) return '';
        return `+${digits}`;
      }

      isValidPhone(phone) {
        return /^\+[1-9]\d{6,14}$/.test(phone);
      }

      isValidEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      }

      getNormalizedEmail() {
        return String(this.emailInput?.value || '').trim().toLowerCase();
      }

      onDeferChange() {
        const deferred = this.deferCheckbox?.checked;
        this.contentCheckboxes.forEach((cb) => {
          cb.disabled = deferred;
          if (deferred) cb.checked = false;
        });
      }

      showFormError(message) {
        if (!this.formError) return;
        this.formError.textContent = message;
        this.formError.hidden = false;
        this.formError.setAttribute('tabindex', '-1');
        this.formError.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        this.formError.focus();
      }

      clearFormError() {
        if (!this.formError) return;
        this.formError.textContent = '';
        this.formError.hidden = true;
      }

      showError(stepEl, message) {
        const errorEl = stepEl.querySelector('[data-step-error]');
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
          errorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }

      clearError(stepEl) {
        const errorEl = stepEl.querySelector('[data-step-error]');
        if (errorEl) {
          errorEl.textContent = '';
          errorEl.hidden = true;
        }
      }

      validateStep(step) {
        const strings = this.getStrings();
        const stepEl = this.querySelector(`[data-step="${step}"]`);
        if (!stepEl) return true;

        this.clearError(stepEl);

        if (step === 1) {
          const input = stepEl.querySelector('[name="aha_preferred_name"]');
          if (!input?.value.trim()) {
            this.showError(stepEl, strings.errorRequired);
            input?.focus();
            return false;
          }
        }

        if (step === 2) {
          const checked = stepEl.querySelector('[name="aha_age_group"]:checked');
          if (!checked) {
            this.showError(stepEl, strings.errorSelect);
            return false;
          }
        }

        if (step === 3) {
          const checked = stepEl.querySelector('[name="aha_dominant_trix"]:checked');
          if (!checked) {
            this.showError(stepEl, strings.errorSelect);
            return false;
          }
        }

        if (step === 4) {
          const phone = this.getNormalizedPhone();
          if (!phone || !this.isValidPhone(phone)) {
            this.showError(stepEl, strings.errorPhone);
            this.phoneInput?.focus();
            return false;
          }
          if (!this.otpToken || this.otpVerifiedPhone !== phone) {
            this.showError(stepEl, strings.errorOtpRequired);
            this.otpInput?.focus();
            return false;
          }
        }

        if (step === 5) {
          const email = this.getNormalizedEmail();
          if (!email) {
            this.showError(stepEl, strings.errorRequired);
            this.emailInput?.focus();
            return false;
          }
          if (!this.isValidEmail(email)) {
            this.showError(stepEl, strings.errorEmail);
            this.emailInput?.focus();
            return false;
          }
        }

        if (step === 6) {
          const checked = stepEl.querySelector('[name="aha_language_preference"]:checked');
          if (!checked) {
            this.showError(stepEl, strings.errorSelect);
            return false;
          }
        }

        if (step === 7) {
          const consent = stepEl.querySelector('[name="aha_consent"]');
          if (!consent?.checked) {
            this.showError(stepEl, strings.errorConsent);
            return false;
          }

          const deferred = this.deferCheckbox?.checked;
          const anyContent = Array.from(this.contentCheckboxes).some((cb) => cb.checked);
          if (!deferred && !anyContent) {
            this.showError(stepEl, strings.errorContent);
            return false;
          }
        }

        return true;
      }

      goNext() {
        if (!this.validateStep(this.currentStep)) return;
        if (this.currentStep < this.totalSteps) {
          this.currentStep++;
          this.updateStep();
        }
      }

      goBack() {
        if (this.currentStep > 1) {
          this.currentStep--;
          this.updateStep();
        }
      }

      updateStep() {
        this.steps.forEach((step) => {
          const num = parseInt(step.dataset.step, 10);
          step.classList.toggle('is-active', num === this.currentStep);
          step.setAttribute('aria-hidden', num !== this.currentStep);
        });

        if (this.progressFill) {
          this.progressFill.style.width = `${(this.currentStep / this.totalSteps) * 100}%`;
        }

        this.updateProgressLabel();

        if (this.backBtn) {
          this.backBtn.hidden = this.currentStep === 1;
        }
        if (this.nextBtn) {
          const onFinalStep = this.currentStep === this.totalSteps;
          const onOtpStepUnverified = this.currentStep === 4 && !this.isOtpVerifiedForCurrentPhone();
          this.nextBtn.hidden = onFinalStep || onOtpStepUnverified;
        }
        if (this.submitBtn) {
          this.submitBtn.hidden = this.currentStep !== this.totalSteps;
        }

        if (this.currentStep === 4) {
          this.setOtpVerifyVisible(this.otpSent && !this.isOtpVerifiedForCurrentPhone());
        }

        const activeStep = this.querySelector(`[data-step="${this.currentStep}"]`);
        const focusTarget = activeStep?.querySelector('input, textarea, button');
        focusTarget?.focus();
      }

      getContentPreferences() {
        if (this.deferCheckbox?.checked) {
          return this.lang === 'en' ? 'Set later' : '之後再設定';
        }
        return Array.from(this.contentCheckboxes)
          .filter((cb) => cb.checked)
          .map((cb) => cb.value)
          .join(', ');
      }

      buildPayload() {
        const consent = this.querySelector('[name="aha_consent"]');
        return {
          aha_language_preference:
            this.querySelector('[name="aha_language_preference"]:checked')?.value || '',
          aha_preferred_name: this.querySelector('[name="aha_preferred_name"]')?.value.trim() || '',
          aha_age_group: this.querySelector('[name="aha_age_group"]:checked')?.value || '',
          aha_dominant_trix: this.querySelector('[name="aha_dominant_trix"]:checked')?.value || '',
          aha_phone: this.getNormalizedPhone(),
          email: this.getNormalizedEmail(),
          aha_receive_content_preference: this.getContentPreferences(),
          aha_consent: Boolean(consent?.checked),
          accepts_marketing: Boolean(consent?.checked),
          otp_token: this.otpToken,
        };
      }

      showSuccess() {
        this.classList.add('is-success');
        if (this.successEl) this.successEl.hidden = false;
        if (this.formWrap) this.formWrap.hidden = true;
        this.successEl?.querySelector('h2')?.focus();
      }

      async onSubmit(e) {
        e.preventDefault();
        this.clearFormError();

        const strings = this.getStrings();

        for (let step = 1; step <= this.totalSteps; step++) {
          if (!this.validateStep(step)) {
            this.currentStep = step;
            this.updateStep();
            return;
          }
        }

        if (!this.apiUrl) {
          this.showFormError(strings.errorApiMissing);
          return;
        }

        if (this.submitBtn) {
          this.submitBtn.classList.add('aha-registration__submit-loading');
          this.submitBtn.disabled = true;
        }

        const headers = this.getRequestHeaders();

        try {
          const response = await fetch(this.apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(this.buildPayload()),
          });

          const data = await response.json().catch(() => ({}));

          if (!response.ok || data.ok === false) {
            const message = this.parseRegisterError(data, strings.errorSubmit);
            this.showFormError(message);

            if (this.hasPhoneRegisterError(data)) {
              this.currentStep = 4;
              this.updateStep();
              this.showError(this.querySelector('[data-step="4"]'), message);
            } else if (this.hasEmailRegisterError(data)) {
              this.currentStep = 5;
              this.updateStep();
              this.showError(
                this.querySelector('[data-step="5"]'),
                response.status === 409 ? strings.errorDuplicateEmail : message
              );
            }

            return;
          }

          this.showSuccess();
        } catch (_err) {
          this.showFormError(strings.errorSubmit);
        } finally {
          if (this.submitBtn) {
            this.submitBtn.classList.remove('aha-registration__submit-loading');
            this.submitBtn.disabled = false;
          }
        }
      }
    }
  );
}
